import React, { useMemo, useState } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
// @ts-ignore
import CircularProgress from "@mui/material/CircularProgress";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DeleteIcon from "@mui/icons-material/DeleteSharp";
import {
  Box,
  Button,
  Grid,
  Pagination,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import DownloadContent from "../common/DownloadContent";
import {
  ContestActivity,
  ContestVote,
  createContestActivityVoteForUser,
  deleteContestActivityVoteForUser,
  deleteContestActivityById,
  getLeaderboardByContestId
} from "../apis/contestApi";

import Leaderboard from "./Leaderboard";

import "react-photo-view/dist/react-photo-view.css";
import { useRecoilValue } from "recoil";
import { telegramInfoState } from "../store/atom";

const ContestActivities: React.FC<{
  contestId: string;
  userId: string;
  data: ContestActivity[];
  isLoading: boolean;
  userContestVotes: (ContestVote & { contest_id: string })[];
  isExpired: boolean;
  pagination: {
    contestActivitiesCount: number;
    count: number;
    page: number;
    limit: number;
    handleChangePage: (
      event: React.ChangeEvent<unknown>,
      value: number
    ) => void;
  };
  refetchUserContestVotes?: any | (() => null);
  refetchAllContestActivities?: any | (() => null);
}> = ({
  contestId,
  userId,
  data = [],
  isLoading = false,
  userContestVotes = [],
  isExpired = true,
  pagination,
  refetchUserContestVotes,
  refetchAllContestActivities
}) => {
  const telegramIdInfo = useRecoilValue(telegramInfoState);
  const [tabValue, setTabValue] = useState("all");
  const [deletingActivity, setDeletingActivity] = useState<string | null>();

  const userTelegramId = telegramIdInfo?.currentConnectedTelegramId;

  const { data: leaderboard, isFetched: isLeaderboardFetched } = useQuery(
    `getLeaderboardByContestId-${contestId}`,
    () => getLeaderboardByContestId(contestId),
    {
      enabled: !!contestId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnReconnect: false
    }
  );

  const votedActivitiesSet = useMemo(() => {
    return new Set(userContestVotes.map((v) => v.activity_id));
  }, [userContestVotes]);

  const currentUsersSubmissions = useMemo(() => {
    return data.filter(
      (each) => each.user_id === userId || each.user_id == userTelegramId
    );
  }, [userId, userTelegramId, data]);

  const filteredActivities =
    tabValue === "all" ? data : currentUsersSubmissions;

  if (isLoading) {
    return (
      <ContentWrapper>
        <CircularProgress
          sx={{ margin: "0 auto", marginTop: "16px", color: "#3c6dc6" }}
        />
      </ContentWrapper>
    );
  }

  const checkIfVotedAlready = (activityId: string) => {
    return votedActivitiesSet.has(activityId);
  };

  const handleCreateVote = async (
    activityId: string,
    isVotedStatusOldValue: string
  ) => {
    try {
      if (isVotedStatusOldValue === "no") {
        await createContestActivityVoteForUser({
          activityId,
          userId
        });
      } else {
        await deleteContestActivityVoteForUser({
          activityId,
          userId
        });
      }
      if (refetchUserContestVotes) {
        await refetchUserContestVotes();
      }
    } catch (e) {
      //
    }
  };

  const handleDeleteActivity = async () => {
    try {
      if (deletingActivity) {
        await deleteContestActivityById(contestId, deletingActivity);
        if (refetchAllContestActivities) {
          refetchAllContestActivities();
        }
        setDeletingActivity(null);
        toast.success("Content deleted successfully!", {
          autoClose: 5000
        });
      }
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.message ?? "Content delete failed! Try again.";
      toast.error(errorMessage, {
        autoClose: 5000
      });
    }
  };

  const handleTabChange = (value: "all" | "your" | "leaderboard") => {
    setTabValue(value);
  };

  return (
    <ContentWrapper>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Tabs
            value={tabValue}
            onChange={(_e, value) => handleTabChange(value)}
            aria-label="Contents submission tabs"
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ".MuiTab-root": {
                color: "#ffffffcc",
                fontSize: {
                  xs: "11px",
                  sm: "14px"
                }
              },
              ".Mui-selected": {
                color: "#ffe263 !important",
                fontSize: {
                  xs: "11px",
                  sm: "14px"
                }
              }
            }}
          >
            <Tab label="All submissions" value={"all"} />
            {userId && <Tab label="Your submissions" value={"yours"} />}

            {!!leaderboard?.length && (
              <Tab label="Leaderboard" value={"leaderboard"} />
            )}
          </Tabs>
        </Box>

        {/* Pagination */}
        {pagination?.contestActivitiesCount > pagination?.limit && (
          <Box
            display="flex"
            alignItems={"center"}
            justifyContent={"flex-start"}
            sx={{ marginTop: "16px" }}
          >
            <Pagination
              count={Math.ceil(pagination.count)}
              page={pagination.page}
              disabled={tabValue !== "all"}
              onChange={pagination.handleChangePage}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#fff"
                },
                "& .Mui-selected": {
                  bgcolor: tabValue === "all" ? "#ffe263 !important" : "",
                  color: "#245c9c"
                }
              }}
            />
          </Box>
        )}
        {/* Activities */}
        {data.length === 0 && (
          <Typography
            sx={{
              marginTop: "8px",
              color: "#ffe263"
            }}
          >
            Contest doesn't have any participants yet
          </Typography>
        )}
      </Grid>

      {tabValue === "leaderboard" ? (
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          {!!leaderboard?.length ? (
            <Leaderboard leaderboard={leaderboard} />
          ) : null}
        </Grid>
      ) : (
        <>
          <PhotoProvider
            // onVisibleChange={}
            photoClosable={false}
            maskClosable={false}
            pullClosable={true}
            overlayRender={(p) => {
              // If contest is expired already, don't show voting option
              if (isExpired) {
                return;
              }
              const activityId = String(p.overlay);
              const isVotedAlready = checkIfVotedAlready(activityId);
              return (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "50px",
                    left: 0,
                    right: 0,
                    margin: "0 auto"
                  }}
                  display={"flex"}
                  textAlign={"center"}
                  justifyContent={"center"}
                  flexDirection={"column"}
                  zIndex={99999}
                >
                  {!!userTelegramId && (
                    <Box
                      sx={{
                        backgroundColor: "rgba(255, 226, 99, 0.8)",
                        width: "200px",
                        margin: "0 auto",
                        padding: "8px 0px",
                        fontSize: 14,
                        border: "1px solid #245c9c",
                        borderRadius: "32px"
                      }}
                      onClick={() =>
                        handleCreateVote(
                          activityId,
                          isVotedAlready ? "yes" : "no"
                        )
                      }
                    >
                      <Typography
                        color="#fff"
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        fontWeight={700}
                        sx={{ cursor: "pointer" }}
                      >
                        Click to {isVotedAlready ? "unvote" : "vote"}{" "}
                        <FavoriteIcon
                          htmlColor="#fff"
                          color={isVotedAlready ? "primary" : "inherit"}
                          fontSize="medium"
                          sx={{ marginLeft: "2px" }}
                        ></FavoriteIcon>
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            }}
            brokenElement={(p) => (
              <Box
                sx={{ color: "#fff" }}
                display={"flex"}
                flexDirection={"column"}
                textAlign={"center"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <video src={p.src} autoPlay loop muted playsInline />
                <a href={p.src} target="_blank">
                  <small>Click to view if not loaded</small>
                </a>
              </Box>
            )}
          >
            {filteredActivities.map((content: ContestActivity) => {
              const isActivitVotedAlready = checkIfVotedAlready(content.id);
              const ownSubmission = content.user_id === userId;
              return (
                <Grid item key={content.id} xs={6} sm={4} md={4} lg={3}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid white",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      minHeight: "200px",
                      height: "100%",
                      "&:hover": {
                        transform: "scale(1.05)"
                      }
                    }}
                  >
                    <Box
                      height="100%"
                      width="100%"
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        "& img, & video": {
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 2,
                          borderBottom: "none",
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0
                        }
                      }}
                    >
                      <PhotoView
                        key={content.id}
                        src={content.content_link}
                        overlay={content.id}
                        data-activity-id={content.id}
                      >
                        {content.content_link.includes(".mp4") ? (
                          <video
                            src={content.content_link}
                            autoPlay
                            loop
                            muted
                            playsInline
                          />
                        ) : (
                          <img
                            src={content.content_link}
                            alt={content.file_name}
                          />
                        )}
                      </PhotoView>
                    </Box>
                    <Box
                      height="auto"
                      width={"100%"}
                      sx={{
                        position: "relative",
                        backgroundColor: "#36454F",
                        borderRadius: 2
                      }}
                    >
                      <Grid container>
                        <Grid
                          item
                          xs={7}
                          md={8}
                          sx={{
                            ml: 1,
                            mt: 0.5,
                            mb: 0.5,
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          {isExpired ? (
                            <FavoriteIcon
                              htmlColor="transparent"
                              fontSize="small"
                              sx={{
                                marginLeft: "2px",
                                background: "transparent"
                              }}
                            />
                          ) : (
                            <>
                              {!!userTelegramId ? (
                                <Box display={"flex"} alignItems={"center"}>
                                  <FavoriteIcon
                                    htmlColor="#fff"
                                    color={
                                      isActivitVotedAlready
                                        ? "primary"
                                        : "inherit"
                                    }
                                    fontSize="small"
                                    sx={{
                                      marginLeft: "2px",
                                      cursor: "pointer"
                                    }}
                                    onClick={() =>
                                      handleCreateVote(
                                        content.id,
                                        isActivitVotedAlready ? "yes" : "no"
                                      )
                                    }
                                  />
                                  {ownSubmission && (
                                    <DeleteIcon
                                      color={"error"}
                                      fontSize="small"
                                      sx={{
                                        marginLeft: "2px",
                                        cursor: "pointer"
                                      }}
                                      onClick={() =>
                                        setDeletingActivity(content.id)
                                      }
                                    />
                                  )}
                                </Box>
                              ) : (
                                <FavoriteIcon
                                  htmlColor="transparent"
                                  fontSize="small"
                                  sx={{
                                    marginLeft: "2px",
                                    background: "transparent"
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          md={4}
                          sx={{
                            position: "absolute",
                            right: 5,
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            height: "100%",
                            width: "100%"
                          }}
                        >
                          <DownloadContent
                            contentName={content.file_name}
                            contentUrl={content.content_link}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </PhotoProvider>

          <Dialog
            open={!!deletingActivity}
            onClose={() => {
              setDeletingActivity(null);
            }}
            aria-labelledby="draggable-dialog-title"
            PaperProps={{
              style: {
                backgroundColor: "#245c9c",
                color: "#f5f5f5"
              }
            }}
          >
            <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
              Delete content
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "#eee" }}>
                Are you sure you want to delete the selected content? You can
                re-submit your creative content as long as the contest is
                active.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                disableRipple
                onClick={() => {
                  setDeletingActivity(null);
                }}
                sx={{ color: "#f5f5f5", textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteActivity()}
                sx={{ textTransform: "none" }}
                disableRipple
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </ContentWrapper>
  );
};

export default ContestActivities;

const ContentWrapper: React.FC<{ children: any }> = ({ children }) => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "auto",
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        color: "white",
        mb: 5
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          justifyContent: { sm: "flex-start" },
          maxWidth: "100%",
          "& .MuiGrid-item": {},
          marginTop: "0px"
        }}
      >
        {children}
      </Grid>
    </Box>
  );
};
